import { useState, useCallback, useEffect, useMemo, useRef } from "react"
import { AssetAvailabilityCalendarDocument, GetOrdersDocument, Order } from "@hydrologiq/gql-experience/graphql"
import { useLazyQueryWithEndpoint, useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  addDays,
  subDays,
  format as formatDate,
  isValid,
  parseISO,
  isBefore,
  isAfter,
  addMonths,
  subMonths,
} from "date-fns"
import { getPeriodEnd, getPeriodStart } from "./AssetAvailabilityCalendar"

type Granularity = "quarter" | "month" | "week"

interface UseCalendarDataReturn {
  // Data
  displayData: any | null
  ordersData: any

  // Loading states
  isInitialLoad: boolean
  isRefetching: boolean
  ordersLoading: boolean

  // Errors
  error: any
  ordersError: any

  // Date state
  anchorDate: string
  endDate: string
  granularity: Granularity
  loadedDateRange: { start: string; end: string } | null

  // Sidebar state
  isDrawerOpen: boolean
  filteredOrder: Order | null
  highlightedAssetIds: string[]

  // Callbacks
  handleAnchorDateChange: (newAnchorDate: string) => void
  handleCalendarBufferChange: (newBuffer: number) => void
  handleLoadMore: (cursor: string, direction: "forward" | "backward") => void
  handleRefresh: () => void
  handleVisiblePeriodChange: (visibleDate: string) => void
  handleFilterOrder: (order?: Order) => void
  handleClearFilter: () => void
  setIsDrawerOpen: (open: boolean) => void
  setGranularity: (granularity: Granularity) => void
  refetchOrders: () => void
  refetch: any
}

export const useCalendarData = (): UseCalendarDataReturn => {
  // Granularity state (default: "month")
  const [granularity, setGranularity] = useState<Granularity>("month")

  // Single anchor date - represents the start of the visible range (period start)
  // Default to current period start based on granularity
  const now = new Date()
  const [anchorDate, setAnchorDate] = useState(() => {
    const periodStart = getPeriodStart(now, "month", true) // Use default granularity for initial state
    return formatDate(periodStart, "yyyy-MM-dd")
  })

  // Query anchor - only changes when we need to load new data (buffer change, initial load, manual refresh)
  // This keeps queryDates stable during normal navigation
  const [queryAnchorDate, setQueryAnchorDate] = useState(() => {
    const periodStart = getPeriodStart(now, "month") // Use default granularity for initial state
    return formatDate(periodStart, "yyyy-MM-dd")
  })

  // Calculate end date dynamically from anchor (period start) to period end
  const endDate = useMemo(() => {
    const anchor = parseISO(anchorDate)
    if (!isValid(anchor)) {
      const defaultPeriodEnd = getPeriodEnd(getPeriodStart(now, granularity), granularity)
      return formatDate(defaultPeriodEnd, "yyyy-MM-dd")
    }
    const periodStart = getPeriodStart(anchor, granularity)
    const periodEnd = getPeriodEnd(periodStart, granularity)
    return formatDate(periodEnd, "yyyy-MM-dd")
  }, [anchorDate, granularity])

  // Track if we've ever loaded data (to distinguish initial load from refetch)
  const hasLoadedDataRef = useRef(false)

  // Keep a stable reference to the last valid data
  const lastValidDataRef = useRef<any>(null)

  // Accumulated data state - stores merged data across cursor navigations
  const [accumulatedData, setAccumulatedData] = useState<any>(null)
  // Track the full loaded date range (expands as we load more)
  const [loadedDateRange, setLoadedDateRange] = useState<{ start: string; end: string } | null>(null)

  // State for calendar buffer (weeks before/after order dates)
  const [calendarBuffer, setCalendarBuffer] = useState(0)

  // State for orders sidebar and filtering
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [filteredOrder, setFilteredOrder] = useState<Order | null>(null)
  const [highlightedAssetIds, setHighlightedAssetIds] = useState<string[]>([])

  // Calculate query dates based on query anchor (stable) + buffer
  // This only changes when buffer changes, granularity changes, or when we explicitly update queryAnchorDate
  const queryDates = useMemo(() => {
    const anchor = parseISO(queryAnchorDate)

    // Validate anchor date
    if (!isValid(anchor)) {
      console.error("Invalid query anchor date:", queryAnchorDate)
      const now = new Date()
      const currentPeriodStart = getPeriodStart(now, granularity)
      // For buffer, convert to appropriate period units
      let bufferPeriods = calendarBuffer
      if (granularity === "week") {
        bufferPeriods = calendarBuffer // weeks
      } else if (granularity === "quarter") {
        bufferPeriods = Math.max(1, Math.ceil(calendarBuffer / 12)) // convert weeks to quarters (roughly)
      } else {
        bufferPeriods = Math.max(1, Math.ceil(calendarBuffer / 4)) // convert weeks to months (roughly)
      }
      const queryStart = getPeriodStart(
        granularity === "quarter"
          ? subMonths(currentPeriodStart, bufferPeriods * 3)
          : granularity === "month"
            ? subMonths(currentPeriodStart, bufferPeriods)
            : subDays(currentPeriodStart, bufferPeriods * 7),
        granularity,
      )
      const queryEnd = getPeriodEnd(
        granularity === "quarter"
          ? addMonths(currentPeriodStart, bufferPeriods * 3)
          : granularity === "month"
            ? addMonths(currentPeriodStart, bufferPeriods)
            : addDays(currentPeriodStart, bufferPeriods * 7),
        granularity,
      )
      return {
        start: formatDate(queryStart, "yyyy-MM-dd"),
        end: formatDate(queryEnd, "yyyy-MM-dd"),
      }
    }

    // Ensure anchor is at period start
    const anchorPeriodStart = getPeriodStart(anchor, granularity)

    // Add buffer periods on each side
    // Convert calendarBuffer (weeks) to period units
    let bufferPeriods = calendarBuffer
    if (granularity === "week") {
      bufferPeriods = calendarBuffer // weeks
    } else if (granularity === "quarter") {
      bufferPeriods = Math.max(1, Math.ceil(calendarBuffer / 12)) // convert weeks to quarters (roughly)
    } else {
      bufferPeriods = Math.max(1, Math.ceil(calendarBuffer / 4)) // convert weeks to months (roughly)
    }

    const queryStart = getPeriodStart(
      granularity === "quarter"
        ? subMonths(anchorPeriodStart, bufferPeriods * 3)
        : granularity === "month"
          ? subMonths(anchorPeriodStart, bufferPeriods)
          : subDays(anchorPeriodStart, bufferPeriods * 7),
      granularity,
    )
    const queryEnd = getPeriodEnd(
      granularity === "quarter"
        ? addMonths(anchorPeriodStart, bufferPeriods * 3)
        : granularity === "month"
          ? addMonths(anchorPeriodStart, bufferPeriods)
          : addDays(anchorPeriodStart, bufferPeriods * 7),
      granularity,
    )

    return {
      start: formatDate(queryStart, "yyyy-MM-dd"),
      end: formatDate(queryEnd, "yyyy-MM-dd"),
    }
  }, [queryAnchorDate, calendarBuffer, granularity])

  const [getCalendar, { data, loading, error, refetch }] = useLazyQueryWithEndpoint(
    AssetAvailabilityCalendarDocument,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
    },
    "primary",
  )

  const getCalendarData = useCallback(() => {
    getCalendar({
      variables: {
        start: queryDates?.start || "",
        end: queryDates?.end || "",
        page: 1,
        per_page: 100,
      },
    })
      .catch((err) => {
        console.error("Error fetching calendar data:", err)
      })
  }, [getCalendar, queryDates])
  useEffect(() => {
    getCalendarData()
  }, [queryDates])

  // Query dates are now stable - Apollo will automatically refetch when they change
  // They only change when buffer changes or queryAnchorDate is explicitly updated

  // Update refs and accumulate data when we get new data
  useEffect(() => {
    if (data?.calendar) {
      hasLoadedDataRef.current = true
      lastValidDataRef.current = data.calendar

      const newData = data.calendar
      const newDateRange = newData.meta?.date_range

      // If this is the first load, just set the data directly
      if (!accumulatedData) {
        setAccumulatedData(newData)
        if (newDateRange) {
          setLoadedDateRange({ start: newDateRange.start, end: newDateRange.end })
        }
        return
      }

      // Merge new data with existing accumulated data
      // Data structure: { asset: { id, ... }, activities: [...] }
      type CalendarItem = { asset: { id: string;[key: string]: any }; activities: any[] }

      // Check if this is a refetch of the same date range (not loading more data)
      // When refetching, we need to remove assets that are no longer in the results (e.g., deleted assets)
      // An exact date range match indicates a refetch
      const isRefetch =
        loadedDateRange &&
        newDateRange &&
        newDateRange.start === loadedDateRange.start &&
        newDateRange.end === loadedDateRange.end

      // Create a set of asset IDs from new data for efficient lookup
      const newAssetIds = new Set((newData.data || []).map((item: CalendarItem) => item.asset.id))

      // Create a map of existing items by asset.id for efficient lookup
      const existingItemsMap = new Map<string, CalendarItem>(
        accumulatedData.data?.map((item: CalendarItem) => [item.asset.id, item]) || [],
      )

      // If this is a refetch of the same range, remove assets that are no longer in the new data
      if (isRefetch) {
        // Remove assets that are not in the new data
        for (const assetId of existingItemsMap.keys()) {
          if (!newAssetIds.has(assetId)) {
            existingItemsMap.delete(assetId)
          }
        }
      }

      // Merge items - update existing or add new
      for (const newItem of (newData.data || []) as CalendarItem[]) {
        const assetId = newItem.asset.id
        const existingItem = existingItemsMap.get(assetId)

        if (existingItem) {
          // Merge activities for existing asset
          const existingActivities = existingItem.activities || []
          const newActivities = newItem.activities || []

          let mergedActivities: any[]
          if (isRefetch) {
            // Refetch: new data is authoritative; deleted activities are absent from newActivities
            mergedActivities = newActivities
          } else {
            // Load more: additive merge; keep existing, add/update from new
            const existingActivitiesMap = new Map<string, any>(
              existingActivities.map((activity: any) => [activity.id, activity]),
            )
            for (const newActivity of newActivities) {
              existingActivitiesMap.set(newActivity.id, newActivity)
            }
            mergedActivities = Array.from(existingActivitiesMap.values())
          }

          existingItemsMap.set(assetId, {
            ...existingItem,
            asset: { ...existingItem.asset, ...newItem.asset }, // Merge asset data too
            activities: mergedActivities,
          })
        } else {
          // Add new item
          existingItemsMap.set(assetId, newItem)
        }
      }

      // Expand the loaded date range
      let mergedStart = loadedDateRange?.start || newDateRange?.start
      let mergedEnd = loadedDateRange?.end || newDateRange?.end

      if (newDateRange) {
        const newStart = parseISO(newDateRange.start)
        const newEnd = parseISO(newDateRange.end)
        const currentStart = mergedStart ? parseISO(mergedStart) : newStart
        const currentEnd = mergedEnd ? parseISO(mergedEnd) : newEnd

        mergedStart = isBefore(newStart, currentStart) ? newDateRange.start : mergedStart
        mergedEnd = isAfter(newEnd, currentEnd) ? newDateRange.end : mergedEnd
      }

      // Create merged data structure
      const mergedData = {
        ...newData,
        data: Array.from(existingItemsMap.values()),
        meta: {
          ...newData.meta,
          date_range: {
            ...newData.meta?.date_range,
            start: mergedStart,
            end: mergedEnd,
          },
        },
      }

      setAccumulatedData(mergedData)
      setLoadedDateRange({ start: mergedStart!, end: mergedEnd! })
    }
  }, [data]) // Intentionally exclude accumulatedData and loadedDateRange to avoid loops

  // Initial load = loading AND we've never loaded data before
  const isInitialLoad = loading && !hasLoadedDataRef.current

  // Refetching = loading AND we've already loaded data before
  const isRefetching = loading && hasLoadedDataRef.current

  // Use accumulated data which contains all loaded data across cursor navigations
  // CRITICAL: Memoize to prevent creating new object reference on every render
  const displayData = useMemo(() => {
    // Prefer accumulated data (which contains merged results)
    if (accumulatedData) {
      return accumulatedData
    }
    // Fallback to current data if available
    if (data?.calendar) {
      return data.calendar
    }
    // If loading and we have previous data, show it
    if (loading && lastValidDataRef.current) {
      return lastValidDataRef.current
    }
    return null
  }, [accumulatedData, data?.calendar, loading])

  // Track pending anchor date changes that should be applied after refetch completes
  const pendingAnchorDateRef = useRef<string | null>(null)

  // Handle anchor date changes from calendar component
  // Only update anchor date if within loaded range, otherwise wait for refetch to complete
  const handleAnchorDateChange = useCallback(
    (newAnchorDate: string) => {
      // Normalize to period start based on current granularity
      const parsedDate = parseISO(newAnchorDate)
      if (!isValid(parsedDate)) {
        console.error("Invalid anchor date:", newAnchorDate)
        return
      }
      const normalizedDate = parsedDate
      const normalizedDateStr = formatDate(normalizedDate, "yyyy-MM-dd")

      // Only update if anchor actually changed
      if (normalizedDateStr === anchorDate) {
        return
      }

      // Check if new anchor period is within loaded date range
      if (loadedDateRange) {
        const newPeriodStart = normalizedDate
        const newPeriodEnd = getPeriodEnd(newPeriodStart, granularity)
        const loadedStart = parseISO(loadedDateRange.start)
        const loadedEnd = parseISO(loadedDateRange.end)

        // Check if new period is fully within loaded range
        // Period is within range if: newPeriodStart >= loadedStart AND newPeriodEnd <= loadedEnd
        const periodStartTime = newPeriodStart.getTime()
        const periodEndTime = newPeriodEnd.getTime()
        const loadedStartTime = loadedStart.getTime()
        const loadedEndTime = loadedEnd.getTime()

        const isWithinRange = periodStartTime >= loadedStartTime && periodEndTime <= loadedEndTime

        // If within range, update anchor immediately (no refetch needed)
        if (isWithinRange) {
          setAnchorDate(normalizedDateStr)
          pendingAnchorDateRef.current = null
          return
        } else {
          // Outside loaded range: store as pending, will be applied after refetch completes
          pendingAnchorDateRef.current = normalizedDateStr
          // Trigger refetch with new anchor date
          setQueryAnchorDate(normalizedDateStr)
        }
      } else {
        // No loaded range yet: store as pending
        pendingAnchorDateRef.current = normalizedDateStr
      }
    },
    [anchorDate, loadedDateRange, granularity],
  )

  // Apply pending anchor date after data refetch completes
  // This ensures anchor date and view update after successful refetch
  useEffect(() => {
    if (pendingAnchorDateRef.current && !loading && data?.calendar) {
      // Data has been loaded, apply pending anchor date
      const pendingDate = pendingAnchorDateRef.current
      setAnchorDate(pendingDate)
      pendingAnchorDateRef.current = null

      // The view will update automatically because:
      // 1. anchorDate changes -> startDate changes (derived from anchorDate)
      // 2. startDate changes -> getDefaultTimeRange recalculates
      // 3. useEffect watching startDate updates visibleTimeStart/visibleTimeEnd
    }
  }, [loading, data?.calendar, queryAnchorDate])

  // Handle calendar buffer changes from calendar component
  const handleCalendarBufferChange = useCallback(
    (newBuffer: number) => {
      setCalendarBuffer(newBuffer)
      // Update query anchor to current anchor so queryDates recalculates with new buffer
      // This will trigger a refetch with the new buffer range
      setQueryAnchorDate(anchorDate)
    },
    [anchorDate],
  )

  // Handle cursor-based loading for date navigation
  // Extends the loaded range using cursor, merges with existing data
  const handleLoadMore = useCallback(
    (cursor: string, direction: "forward" | "backward") => {
      // Use cursor with refetch to extend the loaded range
      // The backend will adjust the date range based on cursor and direction
      // Don't reset accumulated data - let it merge with new data
      refetch({
        start: queryDates.start,
        end: queryDates.end,
        page: 1,
        per_page: 100,
        cursor: cursor,
        direction: direction,
      })
      // The useEffect will handle merging when new data arrives
    },
    [queryDates, refetch],
  )

  // Handle manual refresh (forced refresh with same dates)
  // Refreshes the current query range
  const handleRefresh = useCallback(() => {
    // Reset accumulated data so we get fresh data
    setAccumulatedData(null)
    setLoadedDateRange(null)

    setQueryAnchorDate(anchorDate)
    getCalendarData()
  }, [queryDates, refetch, anchorDate])

  // Handle visible date change from scroll (syncs date input to visible area)
  // This updates the anchor date to reflect the leftmost visible date as user scrolls
  const handleVisiblePeriodChange = useCallback(
    (visibleDate: string) => {
      // Only update anchor date if within loaded range (no refetch needed)
      // The scroll-based data fetching will handle loading more data if needed
      if (loadedDateRange) {
        const newDate = parseISO(visibleDate)
        const loadedStart = parseISO(loadedDateRange.start)
        const loadedEnd = parseISO(loadedDateRange.end)

        // Check if new date is within loaded range
        const isWithinRange = newDate.getTime() >= loadedStart.getTime() && newDate.getTime() <= loadedEnd.getTime()

        if (isWithinRange && visibleDate !== anchorDate) {
          // Update anchor date without triggering refetch
          setAnchorDate(visibleDate)
        }
      }
    },
    [loadedDateRange, anchorDate],
  )

  // Fetch orders for the sidebar
  const {
    data: ordersData,
    loading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders,
  } = useQueryWithEndpoint(
    GetOrdersDocument,
    {
      variables: {
        page: 1,
        per_page: 50,
      },
      fetchPolicy: "cache-and-network",
    },
    "primary",
  )

  // Handle filter order
  const handleFilterOrder = useCallback(
    (order?: Order) => {
      setFilteredOrder(order ?? null)
      const assetIds = order?.assets?.map((asset) => asset.id) || []
      setHighlightedAssetIds(assetIds)

      // Update anchor date if order start is out of range
      if (order?.start) {
        const orderStart = parseISO(order.start)
        const currentRange = loadedDateRange || { start: anchorDate, end: endDate }
        const currentStart = parseISO(currentRange.start)
        const currentEnd = parseISO(currentRange.end)

        // Check if order dates are outside current calendar range
        const isOutOfRange = orderStart < currentStart || orderStart > currentEnd

        if (isOutOfRange) {
          // Reset accumulated data when jumping to a new date range
          setAccumulatedData(null)
          setLoadedDateRange(null)
          // Set anchor to order start with some buffer before
          const bufferDays = 7
          const newAnchor = subDays(orderStart, bufferDays)
          setAnchorDate(formatDate(newAnchor, "yyyy-MM-dd"))
        }
      }
    },
    [anchorDate, endDate, loadedDateRange],
  )

  // Handle clear filter
  const handleClearFilter = useCallback(() => {
    setFilteredOrder(null)
    setHighlightedAssetIds([])
  }, [])

  return {
    // Data
    displayData,
    ordersData,

    // Loading states
    isInitialLoad,
    isRefetching,
    ordersLoading,

    // Errors
    error,
    ordersError,

    // Date state
    anchorDate,
    endDate,
    granularity,
    loadedDateRange,

    // Sidebar state
    isDrawerOpen,
    filteredOrder,
    highlightedAssetIds,

    // Callbacks
    handleAnchorDateChange,
    handleCalendarBufferChange,
    handleLoadMore,
    handleRefresh,
    handleVisiblePeriodChange,
    handleFilterOrder,
    handleClearFilter,
    setIsDrawerOpen,
    setGranularity,
    refetchOrders,
    refetch,
  }
}
